import {
    Mesh,
    DoubleSide,
    MeshBasicMaterial,
    PlaneGeometry,
    BufferGeometry,
    Float32BufferAttribute,
    LineBasicMaterial,
    LineLoop,
    Vector3,
    SphereGeometry,
    Sprite,
    SpriteMaterial,
} from 'three';

import { VRObject3D } from './VRObject3D';
import { myLocationTexture } from './helpers/texturesAndGeometries';
import { ComponentRegistry } from '../../constants/componentRegistry.js';

class VRInvisibleAction extends VRObject3D {
    constructor(containerName, camera, _texture, x, y, z, model) {
        super(containerName);

        this.objConfig.dragOnlyWhenSelected = true;
        this.objConfig.isResizable = true;
        this.objConfig.isRotatable = true;
        this.objConfig.cubeSize = 20;
        this.objConfig.offset = {
            topLeft: { x: 2, y: -3.5 },
            topRight: { x: -2, y: -3.5 },
            bottomRight: { x: -2, y: 3.5 },
            bottomLeft: { x: 2, y: 3.5 },
        };

        this.camera = camera;
        this.model = model;

        this.material = new MeshBasicMaterial({
            color: 0xff0000,
            side: DoubleSide,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            // opacity: model?.editMode ? 0.3 : 1,
            opacity: .5,
        });

        this.geometry = new PlaneGeometry(64, 64);
        this.mesh = new Mesh(this.geometry, this.material);
        this.mesh.type = 'vrInvisibleAction';
        this.mesh.name = 'vrInvisibleAction';
        this.name = 'vrInvisibleAction';
        this.type = 'vrInvisibleAction';

        this.limits = {
            min: 20,
            max: 600,
            distance: 300,
        };

        const halfSize = 32;
        this.perspectivePosition = {
            topLeft: new Vector3(-halfSize, halfSize, 0),
            topRight: new Vector3(halfSize, halfSize, 0),
            bottomRight: new Vector3(halfSize, -halfSize, 0),
            bottomLeft: new Vector3(-halfSize, -halfSize, 0),
        };
        this.originalPerspectivePosition = {
            topLeft: new Vector3(-halfSize, halfSize, 0),
            topRight: new Vector3(halfSize, halfSize, 0),
            bottomRight: new Vector3(halfSize, -halfSize, 0),
            bottomLeft: new Vector3(-halfSize, -halfSize, 0),
        };

        this.dragCubeCorners = {};
        this.dragSprites = {};

        this.frame = new LineLoop(new BufferGeometry(), new LineBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 1,
            depthWrite: false,
            depthTest: true,
        }));
        // Prevent dragging/selecting from the visual border line itself.
        this.frame.raycast = () => { };
        this.frame.visible = !!model?.editMode;
        this.mesh.add(this.frame);
        this._syncFrameGeometry();

        this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);
        this.add(this.mesh);

        this.mesh.groupDetails = {
            title: $trans('vr-invisible-action'),
            icon: 'crop_square',
            editTitle: $trans('edit-vr-invisible-action'),
            dataLang: 'edit-vr-invisible-action',
        };

        this.editControls();
        this._syncEditMaterials(!!model?.editMode);
        return this;
    }

    /**
     * Edit overlay: plane/frame use depthTest so they show when in front of scene
     * geometry; renderOrder sorts them above most transparent content. Drag handles
     * use depthTest:false separately so they stay on top (see _syncHandleMaterials).
     */
    _syncEditMaterials(inEditMode) {
        this.material.depthTest = true;
        this.material.depthWrite = false;
        this.material.polygonOffset = inEditMode;
        this.material.polygonOffsetFactor = inEditMode ? -1 : 0;
        this.material.polygonOffsetUnits = inEditMode ? -4 : 0;
        this.material.needsUpdate = true;
        this.mesh.renderOrder = inEditMode ? 10 : 0;

        const frameMat = this.frame.material;
        frameMat.depthTest = true;
        frameMat.depthWrite = false;
        frameMat.needsUpdate = true;
        this.frame.renderOrder = inEditMode ? 11 : 0;
    }

    _syncHandleMaterials() {
        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (!cube?.material) return;
            cube.material.depthTest = false;
            cube.material.depthWrite = false;
            cube.material.needsUpdate = true;
            cube.children.forEach((child) => {
                if (!child.material) return;
                child.material.depthTest = false;
                child.material.depthWrite = false;
                child.material.needsUpdate = true;
            });
        });
    }

    _vecFromConfig(val, fallback) {
        if (!val) return fallback.clone();
        const x = Number(val.x);
        const y = Number(val.y);
        const z = val.z !== undefined && val.z !== null ? Number(val.z) : 0;
        if (!Number.isFinite(x) || !Number.isFinite(y)) return fallback.clone();
        return new Vector3(x, y, Number.isFinite(z) ? z : 0);
    }

    _syncFrameGeometry() {
        const pts = this.perspectivePosition;
        const z = 0.02;
        const arr = new Float32Array([
            pts.topLeft.x, pts.topLeft.y, z,
            pts.topRight.x, pts.topRight.y, z,
            pts.bottomRight.x, pts.bottomRight.y, z,
            pts.bottomLeft.x, pts.bottomLeft.y, z,
        ]);
        const geom = this.frame.geometry;
        geom.setAttribute('position', new Float32BufferAttribute(arr, 3));
        geom.attributes.position.needsUpdate = true;
        geom.computeBoundingSphere();
    }

    resetPerspectivePositions(width, height) {
        const halfW = width / 2;
        const halfH = height / 2;
        const corners = {
            topLeft: new Vector3(-halfW, halfH, 0),
            topRight: new Vector3(halfW, halfH, 0),
            bottomRight: new Vector3(halfW, -halfH, 0),
            bottomLeft: new Vector3(-halfW, -halfH, 0),
        };

        if (!this.dbConfig.original) this.dbConfig.original = {};
        Object.entries(corners).forEach(([name, vector]) => {
            this.perspectivePosition[name] = vector.clone();
            this.dbConfig[name] = vector.clone();
            this.dbConfig.original[name] = vector.clone();
            this.originalPerspectivePosition[name] = vector.clone();
        });
    }

    resetPerspective = () => {
        this.removeDragPoints();
        this.updateMeshGeometry(64, 64);
        this.resetPerspectivePositions(64, 64);
        this.applyPerspectiveDragAndDrop();
        this.addDragPoints();
        if (this.model?.editMode && this.isSelected && this.dbConfig?.showDragCorners !== false) {
            this.showDragHandles();
        }
    };

    updateMeshGeometry(width, height) {
        if (this.mesh.geometry) {
            this.mesh.geometry.dispose();
        }
        const geometry = new PlaneGeometry(width, height);
        this.mesh.geometry = geometry;
        this.mesh.geometry.computeBoundingBox();
        this.mesh.geometry.computeBoundingSphere();
        this.mesh.updateMatrixWorld(true);
    }

    applyPerspectiveDragAndDrop() {
        const points = this.perspectivePosition;
        const positionAttribute = this.mesh.geometry.attributes.position;
        const uvAttribute = this.mesh.geometry.attributes.uv;

        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2,
        };

        Object.entries(cornerMappings).forEach(([corner, index]) => {
            if (points[corner]) {
                positionAttribute.setXY(index, points[corner].x, points[corner].y);

                if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
                    const offset = this.objConfig.offset[corner];
                    const xOff = offset?.x || 0;
                    const yOff = offset?.y || 0;
                    this.dragCubeCorners[corner].position.set(
                        points[corner].x + xOff,
                        points[corner].y + (this.objConfig.cubeSize / 2) + yOff,
                        1,
                    );
                }
            }
        });

        uvAttribute.setXY(cornerMappings.topLeft, 0, 1);
        uvAttribute.setXY(cornerMappings.topRight, 1, 1);
        uvAttribute.setXY(cornerMappings.bottomRight, 1, 0);
        uvAttribute.setXY(cornerMappings.bottomLeft, 0, 0);

        positionAttribute.needsUpdate = true;
        uvAttribute.needsUpdate = true;

        this.mesh.geometry.computeBoundingBox();
        this.mesh.geometry.computeBoundingSphere();

        this._syncFrameGeometry();
        this.syncHandleVisuals();
    }

    syncHandleVisuals = () => {
        const sx = this.mesh?.scale?.x || 1;
        const sy = this.mesh?.scale?.y || 1;
        const sz = this.mesh?.scale?.z || 1;
        const invScale = new Vector3(
            sx !== 0 ? 1 / sx : 1,
            sy !== 0 ? 1 / sy : 1,
            sz !== 0 ? 1 / sz : 1,
        );

        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (!cube) return;
            cube.scale.copy(invScale);
            cube.renderOrder = 10000;
        });
        this._syncHandleMaterials();
    };

    updateMeshCorner(cornerName, newPosition) {
        const positionAttribute = this.mesh.geometry.attributes.position;
        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2,
        };

        const offset = this.objConfig.offset[cornerName];
        const xOffset = offset?.x || 0;
        const yOffset = offset?.y || 0;

        const vertexPosition = new Vector3(
            newPosition.x - xOffset,
            newPosition.y - (this.objConfig.cubeSize / 2) - yOffset,
            0,
        );

        this.perspectivePosition[cornerName] = vertexPosition;
        this.dbConfig[cornerName] = vertexPosition.clone();

        const vertexIndex = cornerMappings[cornerName];
        if (vertexIndex !== undefined) {
            positionAttribute.setXYZ(
                vertexIndex,
                vertexPosition.x,
                vertexPosition.y,
                vertexPosition.z,
            );
            positionAttribute.needsUpdate = true;
        }

        this._syncFrameGeometry();
    }

    showDragHandles() {
        if (this.dbConfig?.showDragCorners === false) return;
        Object.values(this.dragSprites).forEach((sprite) => {
            if (sprite) sprite.visible = true;
        });
        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (cube) cube.visible = true;
        });
    }

    hideDragHandles() {
        Object.values(this.dragSprites).forEach((sprite) => {
            if (sprite) sprite.visible = false;
        });
        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (cube) cube.visible = false;
        });
    }

    _editModeOpacity(selected = false) {
        if (!this.model?.editMode) return 0;
        return selected ? 0.5 : 0.3;
    }

    _setPlaneOpacity(opacity) {
        const mat = this.mesh?.material ?? this.material;
        if (mat) {
            mat.opacity = opacity;
            mat.needsUpdate = true;
        }
    }

    onSelect() {
        if (!this.dragCubeCorners.topLeft) {
            this.addDragPoints();
        }
        this.showDragHandles();
        this._setPlaneOpacity(this._editModeOpacity(true));
        super.onSelect();
    }

    onDeselect() {
        this.hideDragHandles();
        this._setPlaneOpacity(this._editModeOpacity(false));
        super.onDeselect();
    }

    applyShowDragCornersSetting() {
        if (!this.model?.editMode) return;
        if (this.dbConfig?.showDragCorners === false) {
            this.hideDragHandles();
        } else if (this.isSelected) {
            this.showDragHandles();
        }
    }

    addDragPoints() {
        if (this.dragCubeCorners.topLeft) {
            return;
        }
        const sphereRadius = this.objConfig.cubeSize / 2;
        const hitRadius = sphereRadius * (this.objConfig.cornerHitAreaScale || 1);
        const cubeGeometry = new SphereGeometry(hitRadius, 8, 8);
        const cubeMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            opacity: 0,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            renderOrder: 999,
        });

        const positionAttribute = this.mesh.geometry.attributes.position;
        if (!positionAttribute || positionAttribute.count < 4) {
            return;
        }

        const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        const cornerIndices = [0, 1, 3, 2];

        this.dragCubeCorners = {};

        cornerIndices.forEach((index, i) => {
            const offset = this.objConfig.offset[cornerNames[i]];

            const x = positionAttribute.getX(index) + (offset?.x || 0);
            const y = positionAttribute.getY(index) + (offset?.y || 0);
            const z = positionAttribute.getZ(index);

            const cube = new Mesh(cubeGeometry, cubeMaterial);
            cube.name = cornerNames[i];
            cube.position.set(x, y + this.objConfig.cubeSize / 2, z + 1);
            cube.renderOrder = 10000;

            const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
            const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
            const spriteMaterial = new MeshBasicMaterial({
                color: 0x6B9D11,
                transparent: true,
                opacity: 0.5,
                depthTest: false,
                depthWrite: false,
            });

            const sprite = new Mesh(spriteGeometry, spriteMaterial);
            sprite.name = `sprite_${cornerNames[i]}`;
            sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
            sprite.raycast = () => { };
            sprite.renderOrder = 10001;

            const spriteIconMaterial = new SpriteMaterial({
                map: myLocationTexture,
                depthTest: false,
                depthWrite: false,
                transparent: true,
            });
            const spriteIcon = new Sprite(spriteIconMaterial);
            spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
            spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
            spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
            spriteIcon.raycast = () => { };
            spriteIcon.renderOrder = 10002;

            cube.add(sprite);
            cube.add(spriteIcon);

            this.dragSprites[sprite.name] = sprite;

            this.mesh.add(cube);
            this.dragCubeCorners[cube.name] = cube;
        });

        this.applyPerspectiveDragAndDrop();
        this.syncHandleVisuals();
        this._syncHandleMaterials();
        this.hideDragHandles();
    }

    setSize(val) {
        super.setSize(val);
        this.syncHandleVisuals();
    }

    removeDragPoints() {
        Object.entries(this.dragSprites).forEach(([name]) => {
            if (this.dragSprites[name]) {
                this.dragSprites[name].parent?.remove(this.dragSprites[name]);
                this.dragSprites[name] = null;
            }
        });
        Object.entries(this.dragCubeCorners).forEach(([name]) => {
            if (this.dragCubeCorners[name]) {
                this.mesh.remove(this.dragCubeCorners[name]);
                this.dragCubeCorners[name] = null;
            }
        });
    }

    /**
     * Invisible planes stay opacity 0 in view; still hint if the hotspot is active.
     */
    isClickableInView() {
        if (this.model?.editMode) return false;
        if (!this.mesh || this.mesh.visible === false) return false;
        if (this.userData?.hiddenBySceneAction) return false;
        if (this.dbConfig?.hideOnStart) return false;
        return true;
    }

    setDbConfig(config) {
        super.setDbConfig(config);
        const inEditMode = !!this.model?.editMode;
        const hiddenByHideOnStart = !inEditMode && !!this.dbConfig?.hideOnStart;
        const hiddenBySceneAction = !inEditMode && !!this.userData?.hiddenBySceneAction;
        this.mesh.visible = !hiddenByHideOnStart && !hiddenBySceneAction;
        this._setPlaneOpacity(inEditMode ? this._editModeOpacity(!!this.isSelected) : 0);
        this._syncEditMaterials(inEditMode);
        this.frame.visible = inEditMode;

        const halfSize = 32;
        const defaults = {
            topLeft: new Vector3(-halfSize, halfSize, 0),
            topRight: new Vector3(halfSize, halfSize, 0),
            bottomRight: new Vector3(halfSize, -halfSize, 0),
            bottomLeft: new Vector3(-halfSize, -halfSize, 0),
        };

        ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
            const prop = this.dbConfig[name];
            this.perspectivePosition[name] = this._vecFromConfig(prop, defaults[name]);
        });

        this.applyPerspectiveDragAndDrop();

        if (inEditMode) {
            if (!this.dragCubeCorners.topLeft) {
                this.addDragPoints();
            }
            this.applyShowDragCornersSetting();
        } else {
            this.removeDragPoints();
        }
    }
}

ComponentRegistry.register({
    type: 'vrInvisibleAction',
    displayNameKey: 'vr-invisible-action',
    getDisplayName: (obj) => {
        const n = obj?.name != null ? String(obj.name).trim() : '';
        return n || $trans('vr-invisible-action') || 'VR invisible action';
    },
    events: [
        { value: 'click', labelKey: 'event-click' },
        { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
        { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    ],
    actions: [],
});

export { VRInvisibleAction };
